import { type NavigateFunction, useNavigate } from "react-router";

import { PageTitle } from "@/components/page_title/PageTitle";
import { DataEntrySourceNumber } from "@/components/ui/Badge/DataEntrySourceNumber";
import { Loader } from "@/components/ui/Loader/Loader";
import type { Message } from "@/hooks/messages/MessagesContext";
import { useMessages } from "@/hooks/messages/useMessages";
import { useNumericParam } from "@/hooks/useNumericParam";
import { useUsers } from "@/hooks/user/useUsers";
import { t } from "@/i18n/translate";
import type { DataEntryGetDifferencesResponse, DataEntryStatusName } from "@/types/generated/openapi";
import { cn } from "@/utils/classnames";
import { getDataEntrySourceNumber } from "@/utils/dataEntrySource";
import { type ResolveOutcome, useDataEntryDifferences } from "../hooks/useDataEntryDifferences";
import cls from "./ResolveDifferences.module.css";
import { ResolveDifferencesForm } from "./ResolveDifferencesForm";
import { ResolveDifferencesOverview } from "./ResolveDifferencesOverview";
import { ResolveDifferencesTables } from "./ResolveDifferencesTables";

interface ResolveDifferencesNavigation {
  differences: DataEntryGetDifferencesResponse;
  electionId: number;
  dataEntryId: number;
  pushMessage: ReturnType<typeof useMessages>["pushMessage"];
  navigate: NavigateFunction;
  getName: ReturnType<typeof useUsers>["getName"];
}

// Build the success message and destination for a resolved status, or null when the status does
// not complete a resolution.
function resolvedMessageAndPath(
  status: DataEntryStatusName,
  navigation: ResolveDifferencesNavigation,
  outcome: ResolveOutcome,
): { message: Message; path: string } | null {
  const { differences, electionId, dataEntryId, getName } = navigation;
  const number = getDataEntrySourceNumber(differences.source);
  const statusPath = `/elections/${electionId}/status`;

  switch (status) {
    case "first_entry_has_errors":
      return {
        message: {
          title: t("data_entry_detail.resolve_errors.differences_resolved", { number }),
          text: t("data_entry_detail.resolve_errors.alert_contains_errors"),
        },
        path: `${statusPath}/${dataEntryId}/detail`,
      };
    case "first_entry_finalised": {
      let text = t("election_status.success.data_entry_kept");
      if (outcome.keptUserId) {
        text += ` ${t("election_status.success.choose_another_typist", { typist: getName(outcome.keptUserId) })}`;
      }
      return {
        message: {
          title: t("election_status.success.differences_resolved", { number }),
          text: text,
        },
        path: statusPath,
      };
    }
    case "first_entry_correction":
    case "second_entry_correction":
      return {
        message: {
          title: t("election_status.success.differences_resolved", { number }),
          text: t("election_status.success.data_entry_corrected", { typist: getName(outcome.wrongUserId) }),
        },
        path: statusPath,
      };
    case "empty":
      return {
        message: {
          title: t("election_status.success.differences_resolved", { number }),
          text: t("election_status.success.data_entries_discarded", { number }),
        },
        path: statusPath,
      };
    default:
      return null;
  }
}

// Push the result message and navigate away after resolving differences
function navigateAfterResolve(
  navigation: ResolveDifferencesNavigation,
  status: DataEntryStatusName,
  outcome: ResolveOutcome,
) {
  const result = resolvedMessageAndPath(status, navigation, outcome);
  if (result) {
    navigation.pushMessage(result.message);
    void navigation.navigate(result.path);
  }
}

export function ResolveDifferencesPage() {
  const { pushMessage } = useMessages();
  const navigate = useNavigate();
  const { getName } = useUsers();
  const dataEntryId = useNumericParam("dataEntryId");
  const { election, loading, differences, dataEntryStructure, formState, onSubmit } = useDataEntryDifferences(
    dataEntryId,
    afterSave,
  );

  function afterSave(status: DataEntryStatusName, outcome: ResolveOutcome) {
    if (differences) {
      const context = { differences, electionId: election.id, dataEntryId, pushMessage, navigate, getName };
      navigateAfterResolve(context, status, outcome);
    }
  }

  if (loading || differences === null || dataEntryStructure === null) {
    return <Loader />;
  }

  const { first_entry, first_entry_origin, second_entry, second_entry_user_id, source } = differences;

  return (
    <>
      <PageTitle title={`${t("resolve_differences.page_title")} - Abacus`} />
      <header>
        <section className="smaller-gap">
          <DataEntrySourceNumber>{getDataEntrySourceNumber(source)}</DataEntrySourceNumber>
          <h1>{source.name}</h1>
        </section>
      </header>
      <main className={cls.resolveDifferences}>
        <aside>
          <ResolveDifferencesOverview first={first_entry} second={second_entry} structure={dataEntryStructure} />
        </aside>
        <article>
          <h2>{t("resolve_differences.title")}</h2>
          <p className={cn(cls.description, "mb-xl")}>{t("resolve_differences.page_content")}</p>
          <ResolveDifferencesTables
            first={first_entry}
            second={second_entry}
            structure={dataEntryStructure}
            correctEntry={formState.correctEntry}
          />
          <ResolveDifferencesForm
            firstEntryName={
              first_entry_origin.type === "Typist"
                ? getName(first_entry_origin.user_id)
                : t("resolve_differences.imported_from_file")
            }
            secondEntryName={getName(second_entry_user_id)}
            formState={formState}
            onSubmit={onSubmit}
          />
        </article>
      </main>
    </>
  );
}
